// XP gem tiers by value (owner 2026-11-02: 'for the XP gems, each size up should change colors to mean it's worth more').
// Pure TypeScript (no three, no DOM) so the mapping and the colour rules are unit-tested (tests/gemTier.test.ts) and the
// renderer (pickups.ts), the radar (minimap.ts) and the Encyclopedia (codex.ts) all read ONE definition.
//
// The sim has three XP kinds (0 Small < 3 XP, 1 Medium 3-9, 2 Large >= 10; src/core/sim.ts killEnemy). A fourth, render-only tier
// "Huge" is a Large gem that carries HUGE_XP or more (elite brutes / stompers / wardens, late-game wardens, merged piles). The sim is
// untouched: this only reads Gem.kind and Gem.value.
import { CB, type CbMode, type PickupKey } from './a11y';

/** sim thresholds, restated here only for the Encyclopedia text (killEnemy: xp >= 10 ? Large : xp >= 3 ? Medium : Small) */
export const XP_MEDIUM = 3, XP_LARGE = 10;
/** a Large gem worth at least this much is drawn as the fourth, Huge tier */
export const HUGE_XP = 40;

export const TIER_COUNT = 4;
export type XpTier = 0 | 1 | 2 | 3;
export const TIER_NAME = ['Small', 'Medium', 'Large', 'Huge'] as const;
export const TIER_KEY: readonly PickupKey[] = ['xpSmall', 'xpMedium', 'xpLarge', 'xpHuge'];
/** value range per tier, for text */
export const TIER_RANGE = [`under ${XP_MEDIUM}`, `${XP_MEDIUM} to ${XP_LARGE - 1}`, `${XP_LARGE} to ${HUGE_XP - 1}`, `${HUGE_XP} or more`] as const;
/** shape language per tier (each tier also differs in silhouette, size and glow, so value never rests on hue alone) */
export const TIER_SHAPE = ['a single four-sided shard', 'two prongs on a socket', 'three crystals over a triangular frame', 'a tall six-sided spire ringed by four shards, a gold ring and a gold glint'] as const;

/** the tier of a sim gem: 0..3 for XP, -1 for Salve / Seal / Sweep */
export function xpTier(kind: number, value: number): number {
  if (kind < 0 || kind > 2) return -1;
  return kind === 2 && value >= HUGE_XP ? 3 : kind;
}

/** colour words for the Encyclopedia and QA, per colour-blind mode (the hexes live in a11y.ts CB_PALETTES) */
export const TIER_COLOR_NAME: Record<CbMode, readonly string[]> = {
  off: ['royal blue', 'violet', 'magenta', 'red with gold'],
  pd: ['blue', 'yellow', 'pink-purple', 'white with gold'],
  tritan: ['teal', 'pink', 'white', 'red with gold'],
};

export interface Shades { body: number; light: number; dark: number; ink: number }
const mix = (a: number, b: number, t: number): number => {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - t) + ((b >> s) & 255) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
};
/** facet colours of a tier from its one palette colour: a light face, the body, a dark facet and a near-black ink hull in the same hue */
export function tierShades(hex: number): Shades {
  return { body: hex, light: mix(hex, 0xffffff, 0.58), dark: mix(hex, 0x000000, 0.42), ink: mix(hex, 0x000000, 0.84) };
}

/** the palette colour of a tier in the current colour-blind mode */
export const tierColor = (tier: number): number => CB.pickup(TIER_KEY[tier] ?? 'xpSmall');
/** CSS colour string for the radar / Encyclopedia */
export const hexCss = (hex: number): string => '#' + hex.toString(16).padStart(6, '0');
